import { memo } from 'react';
import sortBy from 'lodash.sortby';
import { format } from 'date-fns';
import Paper from '@mui/material/Paper';
import TableContainer from '@mui/material/TableContainer';
import { Typography } from '@components/Typography';
import { Table } from '@components/Table';
import { useTranslation } from '@hooks';
import { getFullName } from '@helpers/userName';
import { AssignmentType } from '@constants/inbox';
import { formatNumber, formatPercentNumber } from '@helpers/formatNumbers';
import { analyticsService } from '@services';
import ExportCSV from '@pages/AnalyticsPage/ExportCSV';

export const GetTableHeader = () => {
    const { t } = useTranslation();
    return [
        {
            key: 0,
            component: (
                <Typography variant="subtitle2" align="left">
                    {t('common.assignedTo')}
                </Typography>
            ),
        },
        {
            key: 1,
            component: (
                <Typography variant="subtitle2" align="right">
                    {t('AnalyticsConversations.conversationsHandled')}
                </Typography>
            ),
        },
        {
            key: 2,
            component: (
                <Typography align="right" variant="subtitle2">
                    {t('AnalyticsConversations.averageHandleTime')}
                </Typography>
            ),
        },
        {
            key: 3,
            component: (
                <Typography align="right" variant="subtitle2">
                    {t('AnalyticsConversations.resolvedConversations')}
                </Typography>
            ),
        },
        {
            key: 4,
            component: (
                <Typography align="right" variant="subtitle2">
                    {t('AnalyticsConversations.resolutionRate')}
                </Typography>
            ),
        },
    ];
};

export const getTableBody = data => {
    return data.map(assignee => {
        return [
            {
                component: (
                    <Typography variant="subtitle2" align="left">
                        {assignee.name}
                    </Typography>
                ),
            },
            {
                component: (
                    <Typography
                        variant="body2"
                        align="right"
                        sx={{ display: 'flex', width: '100%', justifyContent: 'flex-end', alignItems: 'center' }}
                    >
                        {formatNumber(assignee.conversationsHandled)}
                    </Typography>
                ),
            },
            {
                component: (
                    <Typography
                        variant="body2"
                        align="right"
                        sx={{ display: 'flex', width: '100%', justifyContent: 'flex-end', alignItems: 'center' }}
                    >
                        {formatNumber(assignee.averageHandlingTime)}
                    </Typography>
                ),
            },
            {
                component: (
                    <Typography
                        variant="body2"
                        align="right"
                        sx={{ display: 'flex', width: '100%', justifyContent: 'flex-end', alignItems: 'center' }}
                    >
                        {formatNumber(assignee.resolvedConversations)}
                    </Typography>
                ),
            },
            {
                component: (
                    <Typography
                        variant="body2"
                        align="right"
                        sx={{ display: 'flex', width: '100%', justifyContent: 'flex-end', alignItems: 'center' }}
                    >
                        {formatPercentNumber(assignee.resolutionRate)}
                    </Typography>
                ),
            },
        ];
    });
};

const ConversationsTable = memo(({ isLoading, data, users, agents, filter }) => {
    const { t } = useTranslation();

    const formattedData = data.map(item => {
        const isAgent = item.assigneeType === AssignmentType.Agent;
        const assignee = isAgent
            ? agents.find(a => a.id === item.assigneeId)
            : users.find(u => u.id === +item.assigneeId);
        return {
            ...item,
            name: isAgent ? assignee?.name : getFullName(assignee?.firstName, assignee?.lastName),
        };
    });

    const dataSortedByName = sortBy(formattedData, item => item.name.toLowerCase());

    const onExport = () =>
        analyticsService.exportAnalyticsCSV(
            {
                ...filter,
                locations: filter.locations.map(item => item.id),
                from: filter.timeframe.start ? format(new Date(filter.timeframe.start), 'yyyy-MM-dd') : null,
                to: filter.timeframe.end ? format(new Date(filter.timeframe.end), 'yyyy-MM-dd') : null,
            },
            'conversations',
        );

    return (
        <Paper elevation={1} sx={{ padding: '24px', width: '100%', borderRadius: '4px' }}>
            <div
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}
            >
                <Typography variant="title1" sx={{ alignSelf: 'center' }}>
                    {t(`Header.analyticsConversations`)}
                </Typography>
                <ExportCSV type="conversations" apiCall={onExport} />
            </div>
            <TableContainer>
                <Table columnNames={GetTableHeader()} rows={getTableBody(dataSortedByName)} isDataLoading={isLoading} />
            </TableContainer>
        </Paper>
    );
});

ConversationsTable.displayName = 'ConversationsTable';

export default ConversationsTable;
